<script setup>
/* global axios */
import { ref, computed, watch, onUnmounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAccount } from 'dashboard/composables/useAccount';
import SectionLayout from './SectionLayout.vue';
import Switch from 'next/switch/Switch.vue';

const { accountId } = useAccount();
const { locale } = useI18n();
const enabled = ref(false);
const busy = ref(false);
const error = ref(false);
let generation = 0;
let controller;
onUnmounted(() => { generation += 1; controller?.abort(); });
const russian = computed(() => locale.value.startsWith('ru'));
watch(accountId, async () => {
  const current = ++generation;
  controller?.abort();
  controller = new AbortController();
  enabled.value = false;
  busy.value = true;
  error.value = false;
  try {
    const { data } = await axios.get(`/api/v1/accounts/${accountId.value}/branding`, { signal: controller.signal });
    if (current === generation) enabled.value = data.disable_branding;
  } catch {
    if (current === generation) error.value = true;
  } finally {
    if (current === generation) busy.value = false;
  }
}, { immediate: true });
const save = async () => {
  const current = generation;
  const previous = !enabled.value;
  busy.value = true;
  error.value = false;
  try {
    const { data } = await axios.patch(`/api/v1/accounts/${accountId.value}/branding`, { disable_branding: enabled.value });
    if (current === generation) enabled.value = data.disable_branding;
  } catch {
    if (current === generation) { enabled.value = previous; error.value = true; }
  } finally {
    if (current === generation) busy.value = false;
  }
};
</script>

<template>
  <SectionLayout
    :title="russian ? 'Подпись платформы' : 'Platform attribution'"
    :description="russian ? 'Скрыть подпись платформы в виджетах и центре поддержки этого аккаунта.' : 'Hide platform attribution in this account’s widgets and Help Center.'"
    with-border
  >
    <template #headerActions>
      <Switch v-model="enabled" :disabled="busy || error" @change="save" />
    </template>
    <p v-if="error" role="alert" class="text-n-ruby-11">
      {{ russian ? 'Не удалось получить или сохранить настройку. Обновите страницу и повторите.' : 'Could not load or save the setting. Refresh the page and retry.' }}
    </p>
  </SectionLayout>
</template>
